import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { createWaiver, deleteWaiver, fetchWaivers } from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { useAuth } from "@/hooks/useAuth";
import { formatDate } from "@/lib/format";
import type { WaiverWrite } from "@/types/api";

const EMPTY: WaiverWrite = {
  contaminant_group: "",
  waiver_type: "",
  granted_by: "FDEP",
  effective_from: "",
  expires_at: null,
  note: null,
};

export function WaiversCard({ pwsId }: { pwsId: string }): JSX.Element {
  const auth = useAuth();
  const isPlatform = auth.scope === "platform";
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["waivers", pwsId],
    queryFn: () => fetchWaivers(pwsId),
    enabled: pwsId.length > 0,
  });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<WaiverWrite>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void =>
    void queryClient.invalidateQueries({ queryKey: ["waivers", pwsId] });

  const create = useMutation({
    mutationFn: () => createWaiver(pwsId, draft),
    onSuccess: () => {
      invalidate();
      setAdding(false);
      setDraft(EMPTY);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Save failed"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteWaiver(pwsId, id),
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof ApiError ? e.message : "Delete failed"),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    create.mutate();
  }

  const today = new Date().toISOString().slice(0, 10);
  const waivers = query.data ?? [];

  return (
    <section className="card">
      <h2 className="card-title">
        Monitoring waivers
        <InfoTip
          label="Monitoring waivers"
          body="Waivers granted by FDEP or EPA that reduce how often a contaminant must be sampled (e.g. a VOC waiver). Recording one here documents it; to change the computed schedule, also set the frequency on the affected monitoring-schedule row — a deliberate, human step."
        />
      </h2>

      {query.isLoading ? (
        <div className="skeleton h-10" />
      ) : waivers.length === 0 && !adding ? (
        <p className="text-sm text-ink-300">
          No waivers on record.
          {isPlatform ? " Record one below if DEP or EPA has granted a reduction." : ""}
        </p>
      ) : (
        <ul className="divide-y divide-ink-700">
          {waivers.map((w) => {
            const expired = w.expires_at !== null && w.expires_at < today;
            return (
              <li key={w.id} className="py-2 flex items-center gap-3 flex-wrap text-sm">
                <span className={expired ? "pill-medium" : "pill-ok"}>
                  {expired ? "expired" : "active"}
                </span>
                <span className="text-ink-100">{w.contaminant_group}</span>
                <span className="text-ink-300">{w.waiver_type}</span>
                <span className="text-xs text-ink-400">
                  {w.granted_by} · {formatDate(w.effective_from)} →{" "}
                  {w.expires_at ? formatDate(w.expires_at) : "no expiry"}
                </span>
                {w.note && <span className="text-xs text-ink-400 italic">{w.note}</span>}
                {isPlatform && (
                  <button
                    type="button"
                    className="btn-primary py-0.5 px-2 text-xs ml-auto"
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (window.confirm("Delete this waiver record?")) remove.mutate(w.id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && !adding && (
        <p className="text-sm text-severity-high mt-2" role="alert">
          {error}
        </p>
      )}

      {isPlatform && !adding && (
        <button
          type="button"
          className="btn-primary py-1 px-2 text-xs mt-3"
          onClick={() => setAdding(true)}
        >
          + Record waiver
        </button>
      )}

      {isPlatform && adding && (
        <form onSubmit={onSubmit} className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200">Contaminant group</span>
            <input
              className="input"
              required
              placeholder="e.g. VOC"
              value={draft.contaminant_group}
              onChange={(e) => setDraft({ ...draft, contaminant_group: e.target.value })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200">Waiver type</span>
            <input
              className="input"
              required
              placeholder="e.g. monitoring waiver"
              value={draft.waiver_type}
              onChange={(e) => setDraft({ ...draft, waiver_type: e.target.value })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200">Granted by</span>
            <select
              className="input"
              value={draft.granted_by}
              onChange={(e) => setDraft({ ...draft, granted_by: e.target.value })}
            >
              <option value="FDEP">FDEP</option>
              <option value="EPA">EPA</option>
            </select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200">Effective from</span>
            <input
              type="date"
              className="input"
              required
              value={draft.effective_from}
              onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200">Expires (optional)</span>
            <input
              type="date"
              className="input"
              value={draft.expires_at ?? ""}
              onChange={(e) => setDraft({ ...draft, expires_at: e.target.value || null })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-ink-200">Note (optional)</span>
            <input
              className="input"
              value={draft.note ?? ""}
              onChange={(e) => setDraft({ ...draft, note: e.target.value || null })}
            />
          </label>
          {error && (
            <p className="text-sm text-severity-high sm:col-span-2 lg:col-span-3" role="alert">
              {error}
            </p>
          )}
          <div className="flex gap-2 items-end">
            <button type="submit" className="btn-primary text-sm" disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Save waiver"}
            </button>
            <button
              type="button"
              className="text-sm text-ink-300 hover:text-ink-100"
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
